/**
 * @file AuthContext.tsx
 * @description
 * React Context for managing user authentication state and handling login/logout.
 * Tracks whether the user is authenticated by checking the backend cookie AND local storage for player name.
 * Provides functions to perform login, check status, and logout.
 * Uses the backend '/api/auth/login', '/api/auth/check', and '/api/auth/logout' endpoints.
 * Stores the authenticated player's name in `localStorage` for persistence tied to the cookie state.
 *
 * @dependencies
 * - react: Standard React library for Context.
 */

import React, { createContext, useState, useContext, useCallback, useEffect, ReactNode } from 'react';

// Define the key used for storing the player name in localStorage
const PLAYER_NAME_STORAGE_KEY = 'thegame_player_name';

interface AuthContextType {
  isAuthenticated: boolean | null; // null means checking, boolean is the result (true/false)
  login: (passcode: string, name: string) => Promise<boolean>; // Function to attempt login
  checkAuth: () => Promise<void>; // Function to explicitly check auth status (e.g., on mount)
  logout: () => void; // Function to log out the user
  playerName: string | null; // Store player name
  // Note: Raw passcode is not stored or exposed here for security reasons.
  // Authentication state relies on the cookie set by the backend and local storage name.
}

// Create the context with a default undefined value.
// Using `undefined` allows the `useAuth` hook to check if the provider is missing.
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Custom hook to easily access the AuthContext.
 * Throws an error if used outside of an AuthProvider, guaranteeing a non-undefined context value.
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    // This error indicates a developer mistake: useAuth was called outside the AuthProvider.
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * AuthProvider component that wraps the application and provides the authentication context.
 * Manages the `isAuthenticated` state, `playerName`, and the authentication logic.
 * Authentication requires BOTH a valid backend cookie AND a player name in localStorage.
 */
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // isAuthenticated: null = checking, true = authenticated, false = not authenticated
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  // Initialize local name state with value from localStorage if available
  // Use a callback function for initialization to ensure it only runs once on mount.
  // Ensure initial value is trimmed and defaults to empty string if null.
  const [playerName, setPlayerName] = useState<string | null>(() => {
      try {
          const storedName = localStorage.getItem(PLAYER_NAME_STORAGE_KEY);
          // Ensure the stored name is not just whitespace before setting
          return storedName ? storedName.trim() : null;
      } catch (error) {
          console.error('Failed to read player name from localStorage:', error);
          return null; // Return null if localStorage access fails
      }
  });

  /**
   * Clears player name from state and localStorage.
   */
  const clearPlayerName = useCallback(() => {
       console.log('AuthContext: Clearing player name from state and localStorage.');
       setPlayerName(null);
       try {
            localStorage.removeItem(PLAYER_NAME_STORAGE_KEY);
            console.log('AuthContext: Player name cleared from localStorage.');
       } catch (error) {
           console.error('AuthContext: Failed to clear player name from localStorage:', error);
       }
  }, []);


  /**
   * Checks the authentication status with the backend by calling the /api/auth/check endpoint.
   * This relies on the browser automatically sending the 'passcode' cookie.
   * Authentication succeeds ONLY if the backend validates the cookie AND a player name
   * exists in localStorage.
   * Updates the `isAuthenticated` state.
   */
  const checkAuth = useCallback(async () => {
    console.log('AuthContext: Starting authentication status check...');
    setIsAuthenticated(null); // Indicate that a check is in progress

    // Also retrieve player name from localStorage check immediately
    const nameFromStorage = localStorage.getItem(PLAYER_NAME_STORAGE_KEY);

    // If there's no player name in storage, we cannot consider the user authenticated,
    // even if the cookie is valid. Set isAuthenticated to false and ensure state is clean.
    if (!nameFromStorage) {
        console.log('AuthContext: No player name found in localStorage during check. User is not authenticated.');
        setIsAuthenticated(false);
        clearPlayerName(); // Ensure state is clean (redundant but safe)
         // No need to call backend logout here, the check endpoint itself doesn't require auth
         // and the next login attempt will overwrite the cookie if successful.
        return;
    }

    // If a name exists in storage, proceed to check backend cookie
    try {
      const response = await fetch('/api/auth/check', {
        method: 'GET',
        // 'include' credentials is necessary for the browser to send cookies
        credentials: 'include',
      });

      if (response.ok) {
        console.log('AuthContext: Backend auth check successful (status 200). User authenticated.');
        setIsAuthenticated(true);
        // Player name state should already be set from localStorage initialization/previous login
        // if this checkAuth was triggered after mount/successful login.
        // If it was triggered standalone, the playerName state might not be set yet,
        // but it will be picked up from localStorage by the state initializer on next render or by a dedicated effect if needed.
        // For robustness, let's explicitly set the name from localStorage here if it's not already set in state.
        if (playerName === null) {
            setPlayerName(nameFromStorage.trim());
            console.log('AuthContext: Set playerName state from localStorage during successful check.');
        }
      } else {
        console.log(`AuthContext: Backend auth check failed with status: ${response.status}. User is not authenticated.`);
        setIsAuthenticated(false);
        // If backend check fails, clear any remembered player name and the cookie
        clearPlayerName();
         // Call logout endpoint to explicitly clear cookie
         try {
              await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
              console.log('AuthContext: Called backend logout endpoint to clear cookie after failed auth check.');
         } catch (logoutError) {
              console.error('AuthContext: Failed to call backend logout endpoint during failed auth check:', logoutError);
         }
      }
    } catch (error) {
      console.error('AuthContext: Auth check network error:', error);
      setIsAuthenticated(false); // Assume not authenticated on network error
      // Clear player name and attempt to clear cookie on network error during auth check
      clearPlayerName();
       try {
            await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
            console.log('AuthContext: Called backend logout endpoint to clear cookie after auth check network error.');
       } catch (logoutError) {
            console.error('AuthContext: Failed to call backend logout endpoint during auth check network error:', logoutError);
       }
    }
  }, [clearPlayerName, playerName]); // Added playerName to dependency array to potentially set state inside checkAuth


  /**
   * Attempts to log in by sending the provided passcode to the backend.
   * If successful, updates `isAuthenticated` to true, stores the player name
   * in state and `localStorage`. The backend sets the 'passcode' cookie.
   * Returns a promise that resolves to true on successful login, false otherwise.
   */
  const login = useCallback(async (passcode: string, name: string): Promise<boolean> => {
    const trimmedName = name.trim(); // Ensure name is trimmed before using/storing
    console.log(`AuthContext: Attempting login for "${trimmedName}"...`);
    setIsAuthenticated(null); // Indicate that a login attempt is in progress

    // Ensure player name is valid (not empty after trim) before attempting backend login
    if (!trimmedName) {
        console.warn('AuthContext: Login attempt failed - player name is required.');
        setIsAuthenticated(false);
        clearPlayerName();
        return false;
    }


    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ passcode }),
        // 'include' credentials is necessary for the browser to save the cookie
        credentials: 'include',
      });

      if (response.ok) {
        console.log('AuthContext: Login POST successful (status 200). Passcode accepted.');
        // Immediately set player name in localStorage and state
        setPlayerName(trimmedName); // Set player name in state (trimmed)
        try {
            localStorage.setItem(PLAYER_NAME_STORAGE_KEY, trimmedName); // Store name in localStorage (trimmed)
            console.log('AuthContext: Stored player name in localStorage.');
        } catch (error) {
             console.error('AuthContext: Failed to store player name in localStorage:', error);
             // Decide how to handle localStorage failure. For now, log and proceed.
             // The user might be authenticated via cookie but lose their name.
             // The checkAuth logic relies on name in localStorage, so this could cause issues later.
             // Throwing might be an option here, but let's stick to logging for now.
        }

        // --- FIX: Trigger a full authentication check after successful login ---
        // This re-verifies authentication using both the newly set cookie
        // and the player name in localStorage, ensuring state consistency.
        await checkAuth();
        console.log('AuthContext: Finished re-checking auth after successful login.');

        // checkAuth will set isAuthenticated to true if successful.
        // The Login page's effect watching isAuthenticated will then trigger navigation.
        // Return true based on the outcome of checkAuth.
        // We can check the state directly or wait for checkAuth to implicitly update it.
        // For simplicity, let's just return true here assuming checkAuth will succeed
        // given the cookie was just set and name stored. A more robust way
        // would be to await checkAuth and return based on the *final* isAuthenticated state.
        // Let's just return true, relying on checkAuth to update state correctly.
        return true; // Signal success to the component that called login

      } else {
        console.log(`AuthContext: Login POST failed with status: ${response.status}. Not authenticated.`);
        setIsAuthenticated(false);
         clearPlayerName(); // Clear player name if login fails
         // No need to call logout endpoint here, backend login failed so cookie wasn't set/updated successfully.
        return false; // Signal failure
      }
    } catch (error) {
      console.error('AuthContext: Login POST network error:', error);
      setIsAuthenticated(false); // Assume login failed on network error
       clearPlayerName(); // Clear player name on network error
       // No need to call logout endpoint here for network error on login.
      return false; // Signal failure
    }
  }, [clearPlayerName, checkAuth, playerName]); // Added checkAuth and playerName to dependencies

  /**
   * Logs out the user by clearing local state, localStorage,
   * and explicitly calling the backend logout endpoint to clear the cookie.
   */
  const logout = useCallback(() => {
      console.log('AuthContext: Logging out user...');
      setIsAuthenticated(false); // Signal SocketContext to disconnect
      clearPlayerName(); // Clear player name from state and localStorage

      // Call backend endpoint to explicitly clear the cookie
      fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include', // Include cookies so the server knows which one to clear
      })
      .then(response => {
        if (response.ok) {
          console.log('AuthContext: Backend logout successful.');
        } else {
          console.error('AuthContext: Backend logout failed with status:', response.status);
          // Log error but don't block client-side logout
        }
      })
      .catch(error => {
        console.error('AuthContext: Network error during backend logout:', error);
        // Log error but don't block client-side logout
      });

  }, [clearPlayerName]); // logout depends on clearPlayerName


  // Automatically check authentication status when the provider mounts.
  // This handles cases where the user revisits the site with an existing cookie AND localStorage name.
  // Also re-run checkAuth if isAuthenticated becomes null (e.g. after logout or initial state)
  useEffect(() => {
    console.log(`AuthContext Effect: Running checkAuth. Current isAuthenticated: ${isAuthenticated}`);
     // Only run the automatic check if authentication status is unknown (null)
     if (isAuthenticated === null) {
        checkAuth();
     }
  }, [isAuthenticated, checkAuth]); // Effect depends on isAuthenticated and the memoized checkAuth function


   // Effect to sync playerName state if localStorage changes externally.
   // This is primarily defensive and might not be strictly necessary with current logic,
   // but good practice if other parts of app *could* manipulate localStorage independently.
   // It ensures the React state stays consistent with localStorage for the player name key.
   useEffect(() => {
        const handleStorageChange = (event: StorageEvent) => {
             // Only react to changes for the player name key and originating from *other* windows/tabs
             if (event.key === PLAYER_NAME_STORAGE_KEY && event.storageArea === localStorage) {
                 console.log('AuthContext Effect: localStorage storage event detected for player name key.');
                 try {
                      const storedName = localStorage.getItem(PLAYER_NAME_STORAGE_KEY);
                      const trimmedName = storedName ? storedName.trim() : null;
                      // Update state only if it's different from current state
                      if (playerName !== trimmedName) {
                           console.log('AuthContext Effect: localStorage name changed externally, updating state.');
                           setPlayerName(trimmedName);
                           // If the name was cleared externally, re-run auth check to reflect potentially lost authentication status
                           if (trimmedName === null) {
                               console.log('AuthContext Effect: Player name cleared externally, re-running auth check.');
                               checkAuth();
                           }
                      } else {
                           console.log('AuthContext Effect: localStorage name change detected but state is already in sync.');
                      }
                 } catch (error) {
                      console.error('AuthContext Effect: Failed to read player name from localStorage on storage change:', error);
                 }
            }
        };

       // Add event listener for localStorage changes
        window.addEventListener('storage', handleStorageChange);

       // Cleanup function
        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
        // Depend on checkAuth because it might be called if name is cleared externally.
        // Depend on playerName state to avoid unnecessary re-creation of the handler if playerName changes internally.
   }, [checkAuth, playerName]);


  // Provide the state and functions via the context
  const contextValue: AuthContextType = {
    isAuthenticated,
    login,
    checkAuth,
    logout, // Include logout in the context value
    playerName,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};